import React from "react";
import { Box, Chip, CircularProgress, IconButton, Tooltip } from "@mui/material";
import {
  CheckCircle,
  Warning,
  AddCircleOutline,
  OpenInNew,
} from "@mui/icons-material";
import {
  MatchStatus,
  PatientAddressMatch,
  DoctorMatch,
  ClinicMatch,
  PatientIdentityMatch,
  PatientMatchReason,
} from "./useExtractionMatching";

// ============================================================================
// Generic status chip
// ============================================================================

const STATUS_CONFIG: Record<
  MatchStatus,
  { label: string; color: "success" | "warning" | "info" | "default"; icon: React.ReactNode }
> = {
  matched: { label: "Vorhanden", color: "success", icon: <CheckCircle fontSize="small" /> },
  partial: { label: "Unvollständig", color: "warning", icon: <Warning fontSize="small" /> },
  new: { label: "Neu", color: "info", icon: <AddCircleOutline fontSize="small" /> },
  loading: { label: "Prüfe…", color: "default", icon: <CircularProgress size={14} /> },
  idle: { label: "", color: "default", icon: null },
};

interface MatchChipProps {
  status: MatchStatus;
  tooltip?: string;
  onAction?: () => void;
  actionTooltip?: string;
}

const MatchChip: React.FC<MatchChipProps> = ({ status, tooltip, onAction, actionTooltip }) => {
  if (status === "idle") return null;

  const cfg = STATUS_CONFIG[status];

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Tooltip title={tooltip || cfg.label} arrow>
        <Chip
          size="small"
          icon={cfg.icon as React.ReactElement}
          label={cfg.label}
          color={cfg.color}
          variant="outlined"
          sx={{ height: 24, "& .MuiChip-label": { px: 0.75 } }}
        />
      </Tooltip>
      {onAction && status !== "matched" && status !== "loading" && (
        <Tooltip title={actionTooltip || "Bearbeiten"} arrow>
          <IconButton size="small" onClick={onAction} sx={{ p: 0.25 }}>
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

// ============================================================================
// Patient Address Indicator
// ============================================================================

interface PatientAddressIndicatorProps {
  match: PatientAddressMatch;
  onOpenDialog: () => void;
}

export const PatientAddressIndicator: React.FC<PatientAddressIndicatorProps> = ({
  match,
  onOpenDialog,
}) => {
  const tooltips: Record<MatchStatus, string> = {
    matched: "Adresse ist beim Patienten hinterlegt",
    partial: "Ähnliche Adresse gefunden — bitte prüfen",
    new: "Neue Adresse — kann beim Patienten gespeichert werden",
    loading: "Adressen werden geprüft…",
    idle: "",
  };

  return (
    <MatchChip
      status={match.status}
      tooltip={tooltips[match.status]}
      onAction={onOpenDialog}
      actionTooltip="Adresse beim Patienten speichern"
    />
  );
};

// ============================================================================
// Doctor Indicator
// ============================================================================

interface DoctorIndicatorProps {
  match: DoctorMatch;
  onOpenDialog: () => void;
}

export const DoctorIndicator: React.FC<DoctorIndicatorProps> = ({ match, onOpenDialog }) => {
  const tooltip =
    match.status === "matched"
      ? "Arzt ist im System vorhanden"
      : match.status === "partial"
      ? `Arzt gefunden, fehlende Daten: ${match.missingFields.join(", ")}`
      : match.status === "new"
      ? "Arzt nicht gefunden — kann neu angelegt werden"
      : "";

  return (
    <MatchChip
      status={match.status}
      tooltip={tooltip}
      onAction={onOpenDialog}
      actionTooltip={match.status === "new" ? "Arzt anlegen" : "Arzt aktualisieren"}
    />
  );
};

// ============================================================================
// Clinic Indicator
// ============================================================================

interface ClinicIndicatorProps {
  match: ClinicMatch;
  onOpenDialog: () => void;
}

export const ClinicIndicator: React.FC<ClinicIndicatorProps> = ({ match, onOpenDialog }) => {
  const tooltip =
    match.status === "matched"
      ? "Praxis ist im System vorhanden"
      : match.status === "partial"
      ? `Praxis gefunden, fehlende Daten: ${match.missingFields.join(", ")}`
      : match.status === "new"
      ? "Praxis nicht gefunden — kann neu angelegt werden"
      : "";

  return (
    <MatchChip
      status={match.status}
      tooltip={tooltip}
      onAction={onOpenDialog}
      actionTooltip={match.status === "new" ? "Praxis anlegen" : "Praxis aktualisieren"}
    />
  );
};

// ============================================================================
// Patient Identity Indicator
// ============================================================================

interface PatientIdentityIndicatorProps {
  match: PatientIdentityMatch;
  onOpenSearch: () => void;
  onAutoAssign?: (patientId: number) => void;
}

export const PatientIdentityIndicator: React.FC<PatientIdentityIndicatorProps> = ({
  match,
  onOpenSearch,
  onAutoAssign,
}) => {
  const isAutoMatchable = match.status === "matched" && match.matchedPatientId !== null && !!onAutoAssign;

  const matchReasonLabel: Record<PatientMatchReason, string> = {
    "insurance-number": "Versichertennr.",
    "name-dob": "Name + Geb.",
    "name-only": "Name",
    "dob-fuzzy-name": "Geb. + ähnlicher Name",
  };

  const tooltip =
    isAutoMatchable
      ? `Patient gefunden: ${match.matchedLastName ?? ""}, ${match.matchedFirstName ?? ""}${
          match.matchReason ? ` (${matchReasonLabel[match.matchReason]})` : ""
        } — klicken zum Zuordnen`
      : match.status === "matched"
      ? "Patient zugeordnet"
      : match.status === "partial" && match.matchReason === "insurance-number"
      ? "Mehrere Patienten mit gleicher Versichertennr. — bitte auswählen"
      : match.status === "partial" && match.matchReason === "dob-fuzzy-name"
      ? `Ähnlicher Name gefunden: ${match.matchedLastName ?? ""}, ${match.matchedFirstName ?? ""} — bitte prüfen`
      : match.status === "partial"
      ? "Ähnliche Patienten gefunden — bitte auswählen"
      : match.status === "new"
      ? "Patient nicht gefunden — bitte suchen oder anlegen"
      : "";

  const handleAction = () => {
    if (isAutoMatchable) {
      onAutoAssign!(match.matchedPatientId!);
    } else {
      onOpenSearch();
    }
  };

  // Patient already assigned — show confirmed chip
  if (match.status === "matched" && !isAutoMatchable) {
    const label = match.matchedLastName
      ? `${match.matchedLastName}, ${match.matchedFirstName}`
      : "Patient zugeordnet";
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          size="small"
          icon={<CheckCircle fontSize="small" />}
          label={label}
          color="success"
          variant="outlined"
          sx={{ height: 24, "& .MuiChip-label": { px: 0.75 } }}
        />
      </Tooltip>
    );
  }

  // Auto-matchable: show chip + explicit assign button
  if (isAutoMatchable) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title={tooltip} arrow>
          <Chip
            size="small"
            icon={<CheckCircle fontSize="small" />}
            label={`${match.matchedLastName}, ${match.matchedFirstName}`}
            color="success"
            variant="outlined"
            sx={{ height: 24, "& .MuiChip-label": { px: 0.75 } }}
          />
        </Tooltip>
        <Chip
          size="small"
          label="Zuordnen"
          color="primary"
          variant="filled"
          onClick={() => onAutoAssign!(match.matchedPatientId!)}
          sx={{ height: 24, cursor: "pointer", "& .MuiChip-label": { px: 0.75 } }}
        />
      </Box>
    );
  }

  return (
    <MatchChip
      status={match.status}
      tooltip={tooltip}
      onAction={handleAction}
      actionTooltip={match.status === "new" ? "Patient suchen" : "Patient auswählen"}
    />
  );
};
